/** Returns n as a non-negative finite number, or 0 for any invalid value. */
export const safeCount = (val) => {
  if (typeof val === "boolean" || Array.isArray(val)) return 0;
  if (typeof val === "string" && val.trim() === "") return 0;
  const n = Number(val);
  return Number.isFinite(n) && n >= 0 ? n : 0;
};

const hasOwn = (obj, key) =>
  obj != null && Object.prototype.hasOwnProperty.call(obj, key);

const isRecord = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const identifier = (value) => {
  if (typeof value === "number") return Number.isSafeInteger(value) ? String(value) : "";
  return typeof value === "string" ? value.trim() : "";
};

const nameKey = (value) =>
  value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/\s+/g, " ").trim();

export const refereeDisplayName = (value, id) => {
  if (typeof value !== "string") return "";
  const name = value.trim().replace(/\s+/g, " ");
  const ids = Array.isArray(id) ? id : [id];
  if (!name || !/[a-z\u00c0-\u024f]/i.test(name) ||
    ids.some((value) => identifier(value) === name) || Number.isFinite(Number(name))) return "";
  if (/^(?:n[/.]?[ad]\.?|null|undefined|nan|infinity|unknown|desconocid[oa]|pendiente|no disponible|sin (?:nombre|datos|informacion|equipo|competicion|categoria)|(?:equipo )?(?:local|visitante|casa|fuera)|(?:competicion|categoria|equipo)(?: desconocid[oa]| sin nombre| no disponible)?|competition unavailable|unknown competition|not available)$/.test(nameKey(name))) return "";
  return name;
};

const NAME_FIELDS = ["nombre", "nombreEquipo", "name", "nombreCompeticion", "nombreCategoria", "equipo"];

const nameText = (value, id, depth = 0) => {
  if (typeof value === "string") return refereeDisplayName(value, id);
  if (!isRecord(value) || depth >= 3) return "";
  const ids = [...(Array.isArray(id) ? id : [id]), value.id, value.codigo];
  for (const key of NAME_FIELDS) {
    const name = nameText(value[key], ids, depth + 1);
    if (name) return name;
  }
  return "";
};

const firstName = (id, ...values) => values.map((value) => nameText(value, id)).find(Boolean) || "";

const hasIdentifier = (value) => identifier(value) !== "";

const uniqueName = (names) => {
  const valid = names.filter(Boolean);
  return new Set(valid.map(nameKey)).size === 1 ? valid[0] : "";
};

const compareCompetitionNames = (a, b) =>
  a.localeCompare(b, "es", { sensitivity: "base", numeric: true });

export const normalizeRefereeCompetitions = (
  competitions = [],
  { deduplicateNames = false, selectedId = "" } = {}
) => {
  const byId = new Map();
  (Array.isArray(competitions) ? competitions : []).forEach((competition) => {
    if (!isRecord(competition) || !hasIdentifier(competition.idCompeticion)) return;
    const { idCompeticion } = competition;
    const key = String(idCompeticion).trim();
    const nombre = firstName(idCompeticion, competition.nombreCompeticion, competition.nombre, competition.competicion);
    const existing = byId.get(key);
    if (!existing) byId.set(key, { idCompeticion, names: [nombre] });
    else existing.names.push(nombre);
  });
  const sorted = Array.from(byId.values(), ({ idCompeticion, names }) => ({
    idCompeticion, nombre: uniqueName(names),
  })).sort((a, b) =>
    Number(!a.nombre) - Number(!b.nombre) ||
    compareCompetitionNames(a.nombre, b.nombre) ||
    compareCompetitionNames(String(a.idCompeticion), String(b.idCompeticion))
  );
  if (!deduplicateNames) return sorted;

  return sorted.reduce((unique, competition) => {
    const previous = unique[unique.length - 1];
    if (competition.nombre && previous?.nombre &&
      compareCompetitionNames(competition.nombre, previous.nombre) === 0) {
      // Keep the selected ID representable when different IDs share a display name.
      if (String(competition.idCompeticion) === String(selectedId)) {
        unique[unique.length - 1] = competition;
      }
    } else unique.push(competition);
    return unique;
  }, []);
};

const CONTEXT_FIELDS = ["idTemporada", "idCompeticion", "idGrupo"];
const CONTEXT_ALIASES = { idTemporada: "temporada", idCompeticion: "competicion", idGrupo: "grupo" };
const TEAM_ALIASES = {
  Local: ["equipoLocal", "nombreEquipoLocal", "nombreLocal", "equipo_local", "equipoCasa", "local", "casa"],
  Visitante: ["equipoVisitante", "nombreEquipoVisitante", "nombreVisitante", "equipo_visitante", "equipoFuera", "visitante", "fuera"],
};
const TEAM_ID_FIELDS = ["id", "idEquipo", "codEquipo", "codigo"];

const metadataContext = (source, parent = {}) => Object.fromEntries(CONTEXT_FIELDS.map((field) => {
  const alias = source[CONTEXT_ALIASES[field]];
  const aliasId = isRecord(alias) ? alias.id : field === "idTemporada" ? alias : undefined;
  return [field, [source[field], aliasId, parent[field]].find(hasIdentifier)];
}));

const sameContext = (a, b) =>
  CONTEXT_FIELDS.every((field) => identifier(a[field]) === identifier(b[field]));

const sameMatch = (match, doc) => {
  if (!hasIdentifier(match.idTemporada)) return false;
  let overlap = false;
  for (const [matchIds, docIds] of [
    [[match.idPartido, match.id], [doc.idPartido]],
    [[match.codActa, match.codacta], [doc.codActa, doc.codacta]],
  ]) {
    const own = matchIds.map(identifier).filter(Boolean);
    const parent = docIds.map(identifier).filter(Boolean);
    if (!own.length || !parent.length) continue;
    if (!own.some((id) => parent.includes(id))) return false;
    overlap = true;
  }
  return overlap;
};

const teamValues = (source, side) => [
  ...TEAM_ALIASES[side].map((alias) => source[alias]),
  ...TEAM_ALIASES[side].map((alias) => source.equipos?.[alias])
];

const teamIdentifiers = (source, side) => {
  const ids = [source[`idEquipo${side}`], source[`codEquipo${side}`]];
  const collect = (value, depth = 0) => {
    if (!isRecord(value) || depth >= 3) return;
    ids.push(...TEAM_ID_FIELDS.map((key) => value[key]));
    NAME_FIELDS.forEach((key) => collect(value[key], depth + 1));
  };
  teamValues(source, side).forEach((value) => collect(value));
  return [...new Set(ids.map(identifier).filter(Boolean))];
};

const teamName = (source, side) => {
  const ids = teamIdentifiers(source, side);
  return ids.length <= 1 ? firstName(ids, ...teamValues(source, side)) : "";
};

const competitionName = (source, catalog = false) => firstName(
  metadataContext(source).idCompeticion,
  source.nombreCompeticion, ...(catalog ? [source.nombre] : []), source.competicion
);

const categoryName = (source) =>
  firstName([source.idCategoria, source.codigoCategoria], source.nombreCategoria, source.categoria);

// The dropdown catalogue loses season provenance; it cannot label a dated history.
const catalogNames = (catalog, context) => {
  const compatible = (Array.isArray(catalog) ? catalog : []).filter(isRecord).filter((entry) => {
    const candidate = metadataContext(entry);
    return hasIdentifier(context.idCompeticion) &&
      identifier(candidate.idCompeticion) === identifier(context.idCompeticion) &&
      identifier(candidate.idTemporada) === identifier(context.idTemporada) &&
      (!hasIdentifier(candidate.idGrupo) || identifier(candidate.idGrupo) === identifier(context.idGrupo));
  });
  return {
    nombreCompeticion: uniqueName(compatible.map((entry) => competitionName(entry, true))),
    nombreCategoria: uniqueName(compatible.map(categoryName)),
  };
};

const normalizeMatch = (partido, doc, catalog) => {
  const docContext = metadataContext(doc);
  const context = metadataContext(partido, docContext);
  const parent = sameContext(context, docContext) ? doc : {};
  const inheritedTeams = sameMatch({ ...partido, ...context }, parent) ? parent : {};
  const competitions = (Array.isArray(doc.competicionesDetalle) ? doc.competicionesDetalle : [])
    .filter(isRecord)
    .filter((item) => hasIdentifier(metadataContext(item).idCompeticion) &&
      sameContext(metadataContext(item, docContext), context));
  const fallback = catalogNames(catalog, context);
  const resolveTeam = (side) => {
    const ownName = teamName(partido, side);
    if (ownName) return ownName;
    const ids = new Set([
      ...teamIdentifiers(partido, side),
      ...teamIdentifiers(inheritedTeams, side),
    ]);
    // A shared ID cannot override a contradiction in another team alias.
    return ids.size <= 1 ? teamName(inheritedTeams, side) : "";
  };

  return {
    ...partido,
    ...context,
    equipoLocal: resolveTeam("Local"),
    equipoVisitante: resolveTeam("Visitante"),
    nombreCompeticion: competitionName({ ...partido, ...context }) || competitionName(parent) ||
      uniqueName(competitions.map((entry) => competitionName(entry, true))) || fallback.nombreCompeticion,
    nombreCategoria: categoryName(partido) || categoryName(parent) ||
      uniqueName(competitions.map(categoryName)) || fallback.nombreCategoria,
  };
};

/** Returns true when val is a valid non-negative number (not null/undefined/NaN/Infinity/negative/boolean/array). */
const isValidCount = (val) => {
  if (val === null || val === undefined) return false;
  if (typeof val === "boolean" || Array.isArray(val)) return false;
  if (typeof val === "string" && val.trim() === "") return false;
  const n = Number(val);
  return Number.isFinite(n) && n >= 0;
};

const emptyCardSummary = () => ({
  amarillas: 0,
  segundasAmarillas: 0,
  rojas: 0,
  puntosDisciplinarios: 0,
  porIntervalo: {},
  porEstado: {},
});

/** Additively merge source map into target map, treating null/invalid values as 0. */
const addMap = (target, source) => {
  Object.entries(source || {}).forEach(([key, value]) => {
    target[key] = (target[key] || 0) + safeCount(value);
  });
};

/** Derive disciplinary points from card counts using weights 1-2-5. */
const derivePoints = (am, sa, ro) =>
  safeCount(am) + safeCount(sa) * 2 + safeCount(ro) * 5;

/** Parse fecha string (ISO, DD-MM-YYYY, DD/MM/YYYY) into a comparable timestamp. Returns 0 on failure. */
const parseFecha = (fecha) => {
  if (!fecha) return 0;
  const s = String(fecha).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const d = new Date(s.length === 10 ? s + "T00:00:00" : s);
    return Number.isFinite(d.getTime()) ? d.getTime() : 0;
  }
  const m = s.match(/^(\d{2})[/-](\d{2})[/-](\d{4})/);
  if (m) {
    const d = new Date(`${m[3]}-${m[2]}-${m[1]}T00:00:00`);
    return Number.isFinite(d.getTime()) ? d.getTime() : 0;
  }
  return 0;
};

/**
 * Get a card field from tarjetas.total when it exists and is valid;
 * otherwise derive from local + visitante.
 */
const getCardField = (tarjetas, field) => {
  const t = tarjetas?.total;
  if (hasOwn(t, field) && isValidCount(t[field])) return safeCount(t[field]);
  return safeCount(tarjetas?.local?.[field]) + safeCount(tarjetas?.visitante?.[field]);
};

/**
 * Merge a document's total map into the summary, then for any key present only
 * in local/visitante (absent from total OR with null/invalid value in total),
 * derive from sides — never doubling.
 */
const mergeCardMap = (summaryMap, totalMap, localMap, visitanteMap) => {
  const totalKeys = new Set();
  Object.entries(totalMap || {}).forEach(([key, value]) => {
    if (isValidCount(value)) {
      summaryMap[key] = (summaryMap[key] || 0) + safeCount(value);
      totalKeys.add(key);
    }
    // null/invalid: don't add to totalKeys → falls through to side derivation
  });
  const sideKeys = new Set([
    ...Object.keys(localMap || {}),
    ...Object.keys(visitanteMap || {}),
  ]);
  for (const key of sideKeys) {
    if (!totalKeys.has(key)) {
      summaryMap[key] =
        (summaryMap[key] || 0) +
        safeCount((localMap || {})[key]) +
        safeCount((visitanteMap || {})[key]);
    }
  }
};

export const aggregateRefereeStatistics = (documents = [], catalog = []) => {
  const safeDocs = Array.isArray(documents) ? documents.filter(isRecord) : [];

  const acc = {
    idArbitro: safeDocs[0]?.idArbitro || "",
    nombre: safeDocs[0]?.nombre || "",
    partidos: { total: 0, victoriasLocal: 0, empates: 0, victoriasVisitante: 0 },
    goles: { local: 0, visitante: 0, total: 0 },
    tarjetas: {
      local: emptyCardSummary(),
      visitante: emptyCardSummary(),
      total: emptyCardSummary(),
    },
    penaltisMarcados: { local: 0, visitante: 0, total: 0 },
    temporadas: new Set(),
    competiciones: new Set(),
    grupos: new Set(),
    _competicionesMap: new Map(),
    partidosDetalle: [],
  };

  const addCompetition = (idCompeticion, nombre, priority) => {
    if (!hasIdentifier(idCompeticion)) return;
    const key = identifier(idCompeticion);
    const existing = acc._competicionesMap.get(key);
    if (!existing) {
      acc._competicionesMap.set(key, { idCompeticion, names: nombre ? [nombre] : [], priority });
    } else if (nombre && (!existing.names.length || priority > existing.priority)) {
      existing.names = [nombre];
      existing.priority = priority;
    } else if (nombre && priority === existing.priority) {
      existing.names.push(nombre);
    }
  };

  safeDocs.forEach((doc) => {
    const partidos = doc.partidos || {};
    const victL = isValidCount(partidos.victoriasLocal)
      ? safeCount(partidos.victoriasLocal)
      : safeCount(partidos.victoriaLocal);
    const emps = isValidCount(partidos.empates)
      ? safeCount(partidos.empates)
      : safeCount(partidos.empate);
    const victV = isValidCount(partidos.victoriasVisitante)
      ? safeCount(partidos.victoriasVisitante)
      : safeCount(partidos.victoriaVisitante);
    acc.partidos.victoriasLocal += victL;
    acc.partidos.empates += emps;
    acc.partidos.victoriasVisitante += victV;
    // Explicit total wins; missing/invalid falls back to sum of outcomes
    acc.partidos.total += hasOwn(partidos, "total") && isValidCount(partidos.total)
      ? safeCount(partidos.total)
      : victL + emps + victV;

    const golesLocal = safeCount(doc.goles?.local);
    const golesVis = safeCount(doc.goles?.visitante);
    acc.goles.local += golesLocal;
    acc.goles.visitante += golesVis;
    acc.goles.total += hasOwn(doc.goles || {}, "total") && isValidCount(doc.goles?.total)
      ? safeCount(doc.goles.total)
      : golesLocal + golesVis;

    const penLocal = safeCount(doc.penaltisMarcados?.local);
    const penVis = safeCount(doc.penaltisMarcados?.visitante);
    acc.penaltisMarcados.local += penLocal;
    acc.penaltisMarcados.visitante += penVis;
    acc.penaltisMarcados.total +=
      hasOwn(doc.penaltisMarcados || {}, "total") && isValidCount(doc.penaltisMarcados?.total)
        ? safeCount(doc.penaltisMarcados.total)
        : penLocal + penVis;

    ["local", "visitante"].forEach((side) => {
      const src = doc.tarjetas?.[side] || {};
      const am = safeCount(src.amarillas);
      const sa = safeCount(src.segundasAmarillas);
      const ro = safeCount(src.rojas);
      acc.tarjetas[side].amarillas += am;
      acc.tarjetas[side].segundasAmarillas += sa;
      acc.tarjetas[side].rojas += ro;
      acc.tarjetas[side].puntosDisciplinarios +=
        hasOwn(src, "puntosDisciplinarios") && isValidCount(src.puntosDisciplinarios)
          ? safeCount(src.puntosDisciplinarios)
          : derivePoints(am, sa, ro);
      addMap(acc.tarjetas[side].porIntervalo, src.porIntervalo);
      addMap(acc.tarjetas[side].porEstado, src.porEstado);
    });

    // Total card fields: use explicit total when valid, else derive from sides
    const docTarjetas = doc.tarjetas || {};
    ["amarillas", "segundasAmarillas", "rojas"].forEach((field) => {
      acc.tarjetas.total[field] += getCardField(docTarjetas, field);
    });
    // puntosDisciplinarios total: explicit wins, else sum side values (which may themselves be derived)
    const totPts = docTarjetas.total?.puntosDisciplinarios;
    if (hasOwn(docTarjetas.total || {}, "puntosDisciplinarios") && isValidCount(totPts)) {
      acc.tarjetas.total.puntosDisciplinarios += safeCount(totPts);
    } else {
      // No explicit total puntosDisciplinarios: check for explicit aggregate card counts first (precedence 2),
      // then fall back to per-side independent resolution (precedence 3).
      const cardFields = ["amarillas", "segundasAmarillas", "rojas"];
      const hasExplicitTotalCounts = cardFields.some((f) => isValidCount(docTarjetas.total?.[f]));
      if (hasExplicitTotalCounts) {
        // Explicit aggregate card total present: resolve via getCardField (explicit total wins, missing falls back to sides)
        const am = getCardField(docTarjetas, "amarillas");
        const sa = getCardField(docTarjetas, "segundasAmarillas");
        const ro = getCardField(docTarjetas, "rojas");
        acc.tarjetas.total.puntosDisciplinarios += derivePoints(am, sa, ro);
      } else {
        // No explicit total card counts: resolve each side independently —
        // derive from counts if the side has any valid count, else retain the side's stored points.
        const resolveSidePoints = (side) => {
          const src = docTarjetas[side] || {};
          if (cardFields.some((f) => isValidCount(src[f]))) {
            return derivePoints(src.amarillas, src.segundasAmarillas, src.rojas);
          }
          return isValidCount(src.puntosDisciplinarios) ? safeCount(src.puntosDisciplinarios) : 0;
        };
        acc.tarjetas.total.puntosDisciplinarios +=
          resolveSidePoints("local") + resolveSidePoints("visitante");
      }
    }
    mergeCardMap(
      acc.tarjetas.total.porIntervalo,
      docTarjetas.total?.porIntervalo,
      docTarjetas.local?.porIntervalo,
      docTarjetas.visitante?.porIntervalo
    );
    mergeCardMap(
      acc.tarjetas.total.porEstado,
      docTarjetas.total?.porEstado,
      docTarjetas.local?.porEstado,
      docTarjetas.visitante?.porEstado
    );

    if (doc.idTemporada != null && doc.idTemporada !== "") acc.temporadas.add(doc.idTemporada);
    if (hasIdentifier(doc.idCompeticion)) acc.competiciones.add(String(doc.idCompeticion));
    if (doc.idGrupo != null && doc.idGrupo !== "") acc.grupos.add(String(doc.idGrupo));

    const docContext = metadataContext(doc);
    addCompetition(docContext.idCompeticion, competitionName(doc), 2);
    addCompetition(docContext.idCompeticion, catalogNames(catalog, docContext).nombreCompeticion, 1);
    if (Array.isArray(doc.competicionesDetalle)) {
      doc.competicionesDetalle.forEach((cd) => {
        if (!isRecord(cd)) return;
        const idCompeticion = metadataContext(cd).idCompeticion;
        if (!sameContext(metadataContext(cd, docContext), { ...docContext, idCompeticion })) return;
        addCompetition(idCompeticion, competitionName(cd, true), 1);
      });
    }

    const detalle = Array.isArray(doc.partidosDetalle) ? doc.partidosDetalle : [];
    detalle.filter(isRecord).forEach((partido) => {
      const normalized = normalizeMatch(partido, doc, catalog);
      acc.partidosDetalle.push(normalized);
      addCompetition(
        normalized.idCompeticion,
        competitionName({ ...partido, idCompeticion: normalized.idCompeticion }),
        3
      );
      addCompetition(normalized.idCompeticion, normalized.nombreCompeticion, 1);
    });
  });

  return {
    idArbitro: acc.idArbitro,
    nombre: acc.nombre,
    partidos: acc.partidos,
    goles: acc.goles,
    tarjetas: acc.tarjetas,
    penaltisMarcados: acc.penaltisMarcados,
    temporadas: Array.from(acc.temporadas).sort((a, b) => b - a),
    competiciones: acc.competiciones.size,
    grupos: acc.grupos.size,
    competicionesDetalle: normalizeRefereeCompetitions(Array.from(
      acc._competicionesMap.values(), ({ idCompeticion, names }) => ({ idCompeticion, nombre: uniqueName(names) })
    )),
    partidosDetalle: acc.partidosDetalle.sort((a, b) => parseFecha(b.fecha) - parseFecha(a.fecha)),
  };
};

export const average = (value, total) => {
  if (!total || !Number.isFinite(Number(total))) return 0;
  const result = Number(value || 0) / Number(total);
  return Number.isFinite(result) ? result : 0;
};

export const percentage = (value, total) => {
  if (!total || !Number.isFinite(Number(total))) return 0;
  const result = (Number(value || 0) / Number(total)) * 100;
  return Number.isFinite(result) ? result : 0;
};
